// ── Play Store rating prompt: the eligibility gate ────────────────────────────
// Asks Android users who installed from Google Play to leave a rating, but only
// at a genuine high point (a rare card minted, a streak milestone, a series
// finished) rather than on a timer. Store conversion leans hard on rating COUNT,
// and the listing shipped with zero (checked 2026-08-21), so the first ~20
// ratings are worth more than any amount of traffic pointed at a dead-looking
// page.
//
// WHY LOCALSTORAGE AND NOT ui_prefs. Everything else dismissible moved server-
// side in 16.071 so it follows you across devices. This one deliberately did
// not: you can only rate an app from the device that installed it, and the
// prompt is gated to the Play-installed Android build, so "this device" IS the
// correct scope. A reinstall re-arming the prompt is the intended behaviour, not
// a bug.
import { safeStorage } from './safeStorage.js'

export const PLAY_ID = 'show.nextep.app.twa'
export const PLAY_URL = `https://play.google.com/store/apps/details?id=${PLAY_ID}`
// market:// hands off to the Play app directly instead of bouncing through a
// browser tab. Falls back to PLAY_URL when nothing handles the scheme.
export const PLAY_MARKET_URL = `market://details?id=${PLAY_ID}`

const K_STATE = 'nextep_play_rating_state'   // { outcome, at, asks }
const K_TWA   = 'nextep_is_twa'              // sticky: see isPlayInstall()
// Landing by origin (17.027): decided once per device by whichever first screen
// sees an empty launch, GuestHome (signed out) or HomeDeck (guest/account).
export const LANDING_KEY = 'nextep_landing_variant'

// How long a "not now" buys. Long enough that it never feels like nagging: if
// they say no twice we stop asking entirely (see isEligible).
const SNOOZE_DAYS = 45
const MAX_ASKS = 2

function readState() {
  try { return JSON.parse(safeStorage.get(K_STATE) || '{}') || {} } catch { return {} }
}

function writeState(next) {
  try { safeStorage.set(K_STATE, JSON.stringify(next)) } catch { /* non-fatal */ }
}

export function isAndroid() {
  if (typeof navigator === 'undefined') return false
  return /android/i.test(navigator.userAgent || '')
}

// Did this session come from the Play-installed TWA?
//
// `document.referrer` is the canonical TWA signal, but it is only set on the
// FIRST navigation — after any client-side route change it is gone. So the first
// load that sees it latches a sticky flag, and every later load trusts that.
// Without the latch the prompt could only ever fire on a cold start, which is
// exactly when a user has done nothing worth celebrating yet.
export function isPlayInstall() {
  if (typeof document === 'undefined') return false
  try {
    if ((document.referrer || '').startsWith(`android-app://${PLAY_ID}`)) {
      safeStorage.set(K_TWA, '1')
      return true
    }
  } catch { /* non-fatal */ }
  try { return safeStorage.get(K_TWA) === '1' } catch { return false }
}

// Eligible = Play-installed Android, hasn't already rated, hasn't refused, and
// isn't inside a snooze window.
export function isEligible(now = Date.now()) {
  if (!isAndroid() || !isPlayInstall()) return false
  const s = readState()
  if (s.outcome === 'rated' || s.outcome === 'never') return false
  if ((s.asks || 0) >= MAX_ASKS) return false
  if (s.at && now - s.at < SNOOZE_DAYS * 864e5) return false
  return true
}

export function recordAsked(now = Date.now()) {
  const s = readState()
  writeState({ ...s, at: now, asks: (s.asks || 0) + 1 })
}

// 'rated'  — they tapped through to Play. We can't verify they actually left a
//            star (Play gives us nothing back), so this is "asked and answered":
//            never ask again either way, because re-asking someone who DID rate
//            is the most annoying possible outcome.
// 'later'  — snoozed; may ask once more after SNOOZE_DAYS.
// 'never'  — explicit no. Permanent.
export function recordOutcome(outcome, now = Date.now()) {
  const s = readState()
  writeState({ ...s, outcome, at: now, asks: (s.asks || 0) })
}

export function openPlayListing() {
  if (typeof window === 'undefined') return
  // Try the native Play app first; if the scheme isn't handled the page stays
  // put and the https fallback opens a moment later.
  let fellBack = false
  const fallback = setTimeout(() => { if (!fellBack) window.open(PLAY_URL, '_blank', 'noopener') }, 700)
  try {
    window.location.href = PLAY_MARKET_URL
  } catch {
    fellBack = true
    clearTimeout(fallback)
    window.open(PLAY_URL, '_blank', 'noopener')
  }
}

// ── Which moments count as a high point ───────────────────────────────────────
// Deliberately NOT "finished any show". A first completion is often an import
// artefact or a show they'd already finished years ago, and asking then reads as
// transactional. These are moments where the app just did something FOR them.
//
// 2026-09-23 rewrite of the list. The original three were effectively dead for
// the people this exists for: `nextep:card-minted` was the 16.x cinephile card
// (removed in 17.006), a streak milestone is day 7 at the earliest, and a series
// finale is weeks away for a new install. 24 Play signups in 8 weeks, zero
// prompt events ever (there was no telemetry either). Now: the Final Cut card
// (a real in-app finish, never an import), a first achievement, a streak
// milestone, and the tenth episode marked on this device (a habit, not a moment,
// but an honest one).
export const HIGH_POINTS = ['nextep:keepsake-minted', 'nextep:achievement-unlocked', 'nextep:streak-milestone', 'nextep:finalcut', 'nextep:episode_marked']
export const MARKS_HIGH_POINT = 10
const K_MARKS = 'nextep_play_marks'

// Count a mark on this device; true exactly when it reaches the threshold.
export function noteMark() {
  let n = 0
  try { n = (parseInt(safeStorage.get(K_MARKS) || '0', 10) || 0) + 1; safeStorage.set(K_MARKS, String(n)) } catch { /* non-fatal */ }
  return n === MARKS_HIGH_POINT
}

export function isWorthAsking(eventName, detail) {
  if (eventName === 'nextep:keepsake-minted') return !!detail?.isNew
  if (eventName === 'nextep:episode_marked') return noteMark()
  return true
}
